import { queryOptions } from "@tanstack/react-query";
import { request } from "@/core/lib/api-request";
import type { Area, Level, Permissions } from "@/lib/permissions";

/** Client for `/api/team/*`: the caller's access, members, invitations and roles. */

export type WorkspaceAccess = {
  isOwner: boolean;
  role: { id: string; name: string; preset: string | null } | null;
  /** `null` outside any company — a personal workspace, where everything is yours. */
  permissions: Permissions | null;
};

export type WorkspaceRole = {
  id: string;
  name: string;
  description: string;
  preset: string | null;
  permissions: Permissions;
  memberCount: number;
  inviteCount: number;
};

export type TeamMember = {
  id: string;
  userId: string;
  name: string;
  email: string;
  image: string | null;
  isOwner: boolean;
  roleId: string | null;
  roleName: string;
  createdAt: string;
};

export type TeamInvitation = {
  id: string;
  email: string;
  roleId: string | null;
  roleName: string;
  expiresAt: string;
  createdAt: string;
};

export type RoleCatalog = {
  areas: { key: Area; label: string; levels: Level[] }[];
  presets: { key: string; name: string }[];
};

export const workspaceAccessQuery = (orgId: string | null) =>
  queryOptions({
    queryKey: ["workspace-access", orgId],
    queryFn: () => request<WorkspaceAccess>("/api/team/me"),
    staleTime: 60 * 1000,
  });

export const teamMembersQuery = (orgId: string | null) =>
  queryOptions({
    queryKey: ["team", orgId, "members"],
    queryFn: () =>
      request<{ members: TeamMember[]; invitations: TeamInvitation[] }>("/api/team/members"),
  });

export const teamRolesQuery = (orgId: string | null) =>
  queryOptions({
    queryKey: ["team", orgId, "roles"],
    queryFn: async () => (await request<{ items: WorkspaceRole[] }>("/api/team/roles")).items,
  });

export const roleCatalogQuery = queryOptions({
  queryKey: ["team", "catalog"],
  queryFn: () => request<RoleCatalog>("/api/team/catalog"),
  staleTime: Number.POSITIVE_INFINITY,
});

export function assignMemberRole(memberId: string, roleId: string) {
  return request<{ ok: true }>(`/api/team/members/${encodeURIComponent(memberId)}/role`, {
    method: "PUT",
    body: JSON.stringify({ roleId }),
  });
}

export function inviteTeammate(email: string, roleId: string) {
  return request<{ id: string }>("/api/team/invitations", {
    method: "POST",
    body: JSON.stringify({ email, roleId }),
  });
}

export type RoleInput = {
  name: string;
  description: string;
  permissions: Partial<Permissions>;
};

export function createRole(input: RoleInput) {
  return request<WorkspaceRole>("/api/team/roles", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateRole(id: string, input: Partial<RoleInput>) {
  return request<WorkspaceRole>(`/api/team/roles/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function deleteRole(id: string) {
  return request<void>(`/api/team/roles/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function removeMember(memberId: string) {
  return request<void>(`/api/team/members/${encodeURIComponent(memberId)}`, { method: "DELETE" });
}
